/**
 * E002-T018: AutomationConfigPanel
 *
 * Sidebar panel for configuring automation blocks.
 * Displays Input/Process/Output tabs for comprehensive configuration.
 * Connects directly to BlockModel for persistence.
 *
 * E002-T040: E2E Integration
 * - Reads rows from source Database
 * - Applies filters and mappings
 * - Provides execution context to ExecutionPanel
 */

import { notify, RadioGroup, type RadioItem } from '@affine/component';
import { Button } from '@affine/component/ui/button';
import { useI18n } from '@affine/i18n';
import { createDatabaseBlockDataSource } from '@blocksuite/affine-block-database';
import { ArrowLeftSmallIcon } from '@blocksuite/icons/rc';
import { BRAND_DISPLAY_NAME } from '@streamlain/brand';
import { cssVarV2 } from '@streamlain/theme/v2';
import {
  useLiveData,
  useService,
  useServiceOptional,
} from '@toeverything/infra';
import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useEnableCloud } from '../../../components/hooks/affine/use-enable-cloud';
import {
  SidebarExpandableGroup,
  SidebarExpandableSection,
} from '../../../components/sidebar-expandable-section';
import { PublishWizard } from '../../../desktop/dialogs/marketplace/publish-wizard';
import { flashElementOutline } from '../../../utils/flash-element-outline';
import { AuthService } from '../../cloud';
import { GlobalDialogService } from '../../dialogs';
import { DocsService } from '../../doc';
import { EditorService } from '../../editor';
import { WorkspacePermissionService } from '../../permissions';
import { WorkspaceService } from '../../workspace';
import { useAutomationUIState, useBlockModelConfig } from '../hooks';
import { useFeatureGate } from '../hooks/use-feature-gate';
import { useWorkflowPermissions } from '../hooks/use-workflow-permissions';
import type { AutomationConfig } from '../services/automation';
import {
  AutomationService,
  DEFAULT_STATUS_TRACKING_CONFIG,
} from '../services/automation';
import { AutomationConfigApiService } from '../services/automation-config-api';
import { AutomationDiscoveryService } from '../services/automation-discovery';
import {
  automationSafetyEnableFailureKey,
  isCurrentAutomationSafety,
  presentAutomationSafety,
} from '../services/automation-safety-presentation';
import { AutomationSocketService } from '../services/automation-socket';
import {
  CrossDocAutomationService,
  PermissionDeniedError,
} from '../services/cross-doc-automation';
import type { ColumnInfo } from '../services/database-discovery';
import { DatabaseDiscoveryService } from '../services/database-discovery';
import {
  applyFilters,
  processRowsForExecution,
} from '../services/database-rows';
// Note: ExecutionService is accessed via useAutomationUIState hook (E018-S007-T001)
import type { WorkflowInfo } from '../services/workflow';
import { WorkflowService } from '../services/workflow';
import {
  type AutomationSafetyDiagnosticPage,
  WorkflowClientService,
} from '../services/workflow-client';
import {
  buildAutomationTemplatePublishSource,
  canOpenAutomationTemplatePublish,
} from '../template-publish-source';
import { useRegisterAutomationSessions } from '../tutorial/sessions/automation';
import { usePermissionOverviewTrigger } from '../tutorial/sessions/automation/use-permission-overview-trigger';
import {
  resolveAutomationDetailTitle,
  writeAutomationTitle,
} from '../utils/automation-title';
import { AutomationGroupSelector } from './automation-group-selector';
import * as styles from './automation-panel.css';
import { ExecutionPanel, StatusBadge } from './components/execution-panel';
import { HistoryPanel } from './components/history-panel';
import { OnboardingView } from './components/onboarding-view';
import {
  collectPreFlightDocIds,
  flushPreFlightDocIds,
} from './components/preflight-comparison';
import { computeDatabaseReferenceMoveUpdates } from './database-reference-reconciliation';
import { InputTab } from './input-tab';
import { OutputTab } from './output-tab';
import { ProcessTab } from './process-tab';
import { createSelectColumnOptions } from './select-column-options';
import { WorkspaceAutomationsPanel } from './workspace-automations-panel';

enum AutomationTab {
  Input = 'input',
  Process = 'process',
  Output = 'output',
}

/**
 * Hook to get BlockModel from BlockSuite doc
 * E018-S014-T004: Cross-Doc aware - finds block in current doc or via discovery service
 *
 * @param blockId - Block ID to find
 * @param automationDocId - Doc ID where automation lives (from AutomationDiscoveryService)
 * @param docsService - DocsService for cross-doc lookups
 */
function useAutomationBlockModel(
  blockId: string | null,
  automationDocId: string | undefined,
  docsService: DocsService
) {
  const editorService = useServiceOptional(EditorService);
  const editor = editorService?.editor;
  const editorContainer = useLiveData(editor?.editorContainer$ ?? null);
  const currentDocId = editorContainer?.doc?.id;

  const [crossDocModel, setCrossDocModel] = useState<any>(null);

  // Current doc lookup (synchronous, no side effects)
  const currentDocModel = useMemo(() => {
    if (!blockId) return null;
    if (editorContainer?.doc) {
      const block = editorContainer.doc.getBlock(blockId);
      if (block?.model) return block.model;
    }
    return null;
  }, [blockId, editorContainer]);

  // E018-S014-T004: Cross-doc lookup (async, has side effects from load())
  useEffect(() => {
    if (
      currentDocModel ||
      !blockId ||
      !automationDocId ||
      automationDocId === currentDocId
    ) {
      setCrossDocModel(null);
      return;
    }

    let releaseRef: (() => void) | undefined;
    try {
      const { doc, release } = docsService.open(automationDocId);
      const blockSuiteDoc = doc.blockSuiteDoc;
      if (blockSuiteDoc) {
        blockSuiteDoc.load();
        const block = blockSuiteDoc.getBlock(blockId);
        if (block?.model) {
          setCrossDocModel(block.model);
          releaseRef = release;
        } else {
          release();
        }
      } else {
        release();
      }
    } catch (e) {
      console.error('[AutomationPanel] Cross-doc block lookup failed:', e);
    }
    return releaseRef ? () => releaseRef() : undefined;
  }, [blockId, automationDocId, currentDocId, currentDocModel, docsService]);

  return currentDocModel ?? crossDocModel;
}

// E018-S007-T001: extractConfig moved to hooks/use-block-model-config.ts
// Config is now read reactively via useBlockModelConfig hook

/**
 * AutomationConfigPanel - Main sidebar component
 *
 * Registered via ExtensionSidebarService in streamlain module.
 */
export const AutomationConfigPanel: FC = () => {
  const automationService = useService(AutomationService);
  const automationDiscoveryService = useService(AutomationDiscoveryService);
  const databaseDiscoveryService = useService(DatabaseDiscoveryService);
  const workflowService = useService(WorkflowService);
  const workflowClient = useService(WorkflowClientService);
  // Note: ExecutionService is accessed via useAutomationUIState hook
  const editorService = useServiceOptional(EditorService);
  const workspaceService = useService(WorkspaceService);
  const docsService = useService(DocsService);
  const crossDocAutomationService = useService(CrossDocAutomationService);
  // Initialize socket connection for real-time log updates (side-effect only)
  useService(AutomationSocketService);

  // sl-u319.1.3: Feature gate for marketplace publishing
  const { isFeatureAllowed } = useFeatureGate();
  const publishAllowed = isFeatureAllowed('marketplace-publish');
  const permissionService = useServiceOptional(WorkspacePermissionService);
  const canPublishMarketplace =
    useLiveData(permissionService?.permission.isOwnerOrAdmin$ ?? null) === true;

  // "Open Workflow" callback wired below after config/workflows are available

  // sl-728o.3.1: Register automation tutorial sessions
  useRegisterAutomationSessions();

  // sl-trigger-explicit: Config API for schedule operations
  const configApiService = useService(AutomationConfigApiService);

  // sl-trigger-explicit: Schedule saving state
  const [isScheduleSaving, setIsScheduleSaving] = useState(false);
  const [safetyDiagnostic, setSafetyDiagnostic] =
    useState<AutomationSafetyDiagnosticPage | null>(null);

  const activeBlockId = useLiveData(automationService.activeBlockId$);
  const databases = useLiveData(databaseDiscoveryService.databases$);

  // E024-S001-T001: Check workspace type - Automations only available in Cloud
  const workspace = workspaceService.workspace;
  const isLocalStorage = workspace.flavour === 'local';
  const confirmEnableCloud = useEnableCloud();

  // E024: Auth state for "Sign in and Enable" vs "Enable Cloud" button
  const authService = useService(AuthService);
  const loginStatus = useLiveData(authService.session.status$);
  const isLoggedIn = loginStatus === 'authenticated';
  const globalDialogService = useService(GlobalDialogService);
  const t = useI18n();

  // sl-3nk: Get current user for ownership check (AI settings only editable by owner)
  const currentAccount = useLiveData(authService.session.account$);
  const currentUserId = currentAccount?.id ?? undefined;

  const handleEnableCloud = useCallback(() => {
    if (isLoggedIn) {
      confirmEnableCloud(workspace);
    } else {
      globalDialogService.open('sign-in', {});
    }
  }, [isLoggedIn, confirmEnableCloud, workspace, globalDialogService]);

  // E018-S014-T004: Watch automation info for cross-doc lookup
  const automationInfoLive$ = activeBlockId
    ? automationDiscoveryService.watchAutomation(activeBlockId)
    : null;
  const automationInfo = useLiveData(automationInfoLive$);
  const workflows = useLiveData(workflowService.workflows$);
  // sl-tq87: Schema used for validation bypass (discovery not mandatory when schema present)
  const workflowSchema = useLiveData(workflowService.selectedWorkflowSchema$);

  // Note: executionState is now accessed via useAutomationUIState hook (E018-S007-T001)

  // sl-67k: Persist automation tab selection in localStorage
  const AUTOMATION_TAB_STORAGE_KEY = 'streamlain:automation:activeTab';
  const [currentTab, setCurrentTab] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(AUTOMATION_TAB_STORAGE_KEY);
      if (
        stored &&
        Object.values(AutomationTab).includes(stored as AutomationTab)
      ) {
        return stored;
      }
    }
    return AutomationTab.Input;
  });

  // Persist tab selection to localStorage
  useEffect(() => {
    localStorage.setItem(AUTOMATION_TAB_STORAGE_KEY, currentTab);
  }, [currentTab]);

  // sl-2w9: Tab items for RadioGroup with animated indicator
  const tabItems: RadioItem[] = useMemo(
    () => [
      {
        value: AutomationTab.Input,
        label: t['com.streamlain.streamlain.process.tab-input'](),
      },
      {
        value: AutomationTab.Process,
        label: t['com.streamlain.streamlain.process.tab-process'](),
      },
      {
        value: AutomationTab.Output,
        label: t['com.streamlain.streamlain.process.tab-output'](),
      },
    ],
    [t]
  );

  // Collapsible state for sticky footer sections (must be before any conditional returns)
  const [executionExpanded, setExecutionExpanded] = useState(true);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [publishExpanded, setPublishExpanded] = useState(true);
  const [publishWizardOpen, setPublishWizardOpen] = useState(false);

  // Refs for click-outside detection
  const footerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // sl-trm: Collapse History when clicking INSIDE the sidebar but OUTSIDE the footer
  // This prevents closing when clicking on the canvas or elsewhere outside the sidebar
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Only react to clicks INSIDE the panel but OUTSIDE the footer
      if (
        panelRef.current?.contains(target) &&
        footerRef.current &&
        !footerRef.current.contains(target)
      ) {
        setHistoryExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get BlockModel directly from BlockSuite
  // E018-S014-T004: Pass automationInfo.docId for cross-doc block lookup
  const blockModel = useAutomationBlockModel(
    activeBlockId,
    automationInfo?.docId,
    docsService
  );

  // E018-S007-T001: SINGLE SOURCE OF TRUTH
  // Config is read reactively from BlockModel via subscription to propsUpdated
  // NO local useState<config> - eliminates stale state possibility
  const config = useBlockModelConfig(blockModel);

  useEffect(() => {
    const workflowId = config?.workflowId;
    const docId = automationInfo?.docId;
    if (!activeBlockId || !workflowId || !docId || !workspace.id) {
      setSafetyDiagnostic(null);
      return;
    }
    let current = true;
    void configApiService
      .getConfig(activeBlockId, workspace.id, docId)
      .then(async saved =>
        saved
          ? {
              saved,
              safety: await workflowClient.getAutomationSafety(
                workflowId,
                saved.id
              ),
            }
          : null
      )
      .then(result => {
        if (!current) return;
        if (
          result?.safety &&
          isCurrentAutomationSafety(result.safety, workflowId, result.saved.id)
        ) {
          setSafetyDiagnostic(result.safety);
        } else {
          setSafetyDiagnostic(null);
        }
      })
      .catch(() => {
        if (current) setSafetyDiagnostic(null);
      });
    return () => {
      current = false;
    };
  }, [
    activeBlockId,
    automationInfo?.docId,
    config?.workflowId,
    configApiService,
    workflowClient,
    workspace.id,
  ]);

  // sl-kqzl.5: Fire the permission-overview coachmark once after first setup.
  usePermissionOverviewTrigger(config?.workflowId ?? null);

  // sl-za0v.10: RBAC — check workflow permissions for UI gating
  const { permissions: wfPermissions } = useWorkflowPermissions(
    config?.workflowId ?? null
  );

  const liveWorkflowName = config?.workflowId
    ? workflows.find(w => w.id === config.workflowId)?.name
    : undefined;
  const detailAutomationTitle = resolveAutomationDetailTitle(
    automationInfo?.name,
    {
      title: config?.title,
      titleMode: config?.titleMode,
      workflowName: liveWorkflowName ?? config?.workflowName,
    },
    t['com.streamlain.streamlain.common.new-automation']()
  );
  const [titleDraft, setTitleDraft] = useState(detailAutomationTitle);
  const [isTitleEditing, setIsTitleEditing] = useState(false);

  useEffect(() => {
    if (!isTitleEditing) setTitleDraft(detailAutomationTitle);
  }, [activeBlockId, detailAutomationTitle, isTitleEditing]);

  const commitTitleDraft = useCallback(() => {
    const didWrite = writeAutomationTitle(
      blockModel as { props: Record<string, unknown> } | null,
      titleDraft,
      'user'
    );
    setIsTitleEditing(false);
    if (!didWrite) setTitleDraft(detailAutomationTitle);
  }, [blockModel, detailAutomationTitle, titleDraft]);

  const detailTitleEditor = (
    <input
      className={styles.titleInput}
      value={titleDraft}
      onChange={event => setTitleDraft(event.target.value)}
      onFocus={() => setIsTitleEditing(true)}
      onBlur={commitTitleDraft}
      onKeyDown={event => {
        if (event.key === 'Enter') {
          event.currentTarget.blur();
        } else if (event.key === 'Escape') {
          setTitleDraft(detailAutomationTitle);
          setIsTitleEditing(false);
          event.currentTarget.blur();
        }
      }}
      placeholder={t['com.streamlain.streamlain.common.new-automation']()}
      aria-label={t['com.streamlain.streamlain.common.new-automation']()}
    />
  );

  // Clear stale schema immediately on automation switch to prevent cross-contamination.
  // Without this, selectedWorkflowSchema$ retains the previous automation's schema
  // until the new one loads (or forever if the new automation has no workflow).
  useEffect(() => {
    workflowService.selectedWorkflowSchema$.next(null);
  }, [activeBlockId, workflowService]);

  // Load schema on mount when workflowId exists (Page Reload Fix)
  // This ensures schema is available immediately for Input Tab
  // Primary loading on workflow change happens in process-tab.tsx
  useEffect(() => {
    if (config?.workflowId) {
      workflowService.loadWorkflowSchema(config.workflowId).catch(() => {
        // Schema loading errors are handled in the service
      });
    }
  }, [config?.workflowId, workflowService]);

  // Listen for toggle changes from canvas node
  // The canvas node dispatches 'automation-trigger-sync' when its toggle is clicked
  // E018-S007-T001: No need to manually re-extract config anymore
  // useBlockModelConfig hook automatically re-renders on propsUpdated
  useEffect(() => {
    const handleTriggerSync = (event: Event) => {
      const customEvent = event as CustomEvent<{
        blockId: string;
        triggerConfig: AutomationConfig['triggerConfig'];
      }>;
      // Only update if this event is for our active block
      if (customEvent.detail.blockId === activeBlockId) {
        console.log(
          '[AutomationPanel] Received trigger sync from canvas:',
          customEvent.detail.triggerConfig
        );
        // Config will auto-update via useBlockModelConfig hook subscription
      }
    };

    window.addEventListener('automation-trigger-sync', handleTriggerSync);
    return () => {
      window.removeEventListener('automation-trigger-sync', handleTriggerSync);
    };
  }, [activeBlockId]);

  // Get editor doc for reading database rows
  const editor = editorService?.editor;
  const editorContainer = useLiveData(editor?.editorContainer$ ?? null);
  const currentDoc = editorContainer?.doc;
  const currentDocId = currentDoc?.id;

  // E018-S014-T004: Cross-Doc aware row processing
  // For same-doc: synchronous processing from currentDoc
  // For cross-doc: async loading via CrossDocAutomationService with proper cleanup
  const isCrossDoc = config?.sourceDocId && config.sourceDocId !== currentDocId;

  // State for async cross-doc rows
  const [crossDocRows, setCrossDocRows] = useState<ReturnType<
    typeof processRowsForExecution
  > | null>(null);
  const lastCrossDocErrorKeyRef = useRef<string | null>(null);

  // Effect for cross-doc row loading with proper cleanup
  useEffect(() => {
    if (!isCrossDoc || !config?.sourceDocId || !config?.sourceTableId) {
      setCrossDocRows(null);
      lastCrossDocErrorKeyRef.current = null;
      return;
    }

    let cancelled = false;
    const sourceDocId = config.sourceDocId;
    const sourceTableId = config.sourceTableId;
    const loadKey = `${sourceDocId}:${sourceTableId}`;

    const loadCrossDocRows = async () => {
      try {
        const result = await crossDocAutomationService.getSourceRows(
          sourceDocId,
          sourceTableId
        );

        if (cancelled) return;

        if (result.success && result.rows) {
          lastCrossDocErrorKeyRef.current = null;
          // Transform CrossDocAutomationService result to ProcessedRows format
          const databaseTitle = config.sourceTableName || 'Cross-Doc Database';

          // Create DatabaseRow format for allRows/filteredRows
          const databaseRows = result.rows.map(row => ({
            id: row.rowId,
            rowId: row.rowId,
            cells: row.cells,
          }));

          // sl-czam: Apply filters to cross-doc rows (same as same-doc path)
          const filtered = applyFilters(databaseRows, config.filterGroup);

          // Create a compatible result structure matching ProcessedRows interface
          const processedResult = {
            sourceTable: databaseTitle,
            sourceTableId,
            allRows: databaseRows,
            filteredRows: filtered,
            filteredOutCount: databaseRows.length - filtered.length,
            payloads: result.rows.map(row => ({
              rowId: row.rowId,
              ...row.cells,
            })),
            columns: result.columns || [],
          };

          setCrossDocRows(
            processedResult as unknown as ReturnType<
              typeof processRowsForExecution
            >
          );
        } else {
          const message =
            result.error?.message || 'Could not load cross-document data.';
          const errorKey = `${loadKey}:${result.error?.name ?? 'Error'}:${message}`;
          const shouldReport = lastCrossDocErrorKeyRef.current !== errorKey;
          lastCrossDocErrorKeyRef.current = errorKey;

          if (shouldReport) {
            console.warn(
              '[AutomationPanel] Failed to load cross-doc rows:',
              result.error
            );
          }
          setCrossDocRows(null);

          if (shouldReport) {
            // Show user-facing notification for permission errors
            if (result.error instanceof PermissionDeniedError) {
              notify.error({
                title: 'Access denied',
                message:
                  'You do not have permission to read the source database. ' +
                  'Ask the document owner for access.',
              });
            } else if (result.error) {
              notify.error({
                title: 'Source table unavailable',
                message,
              });
            }
          }
        }
      } catch (e) {
        if (!cancelled) {
          const message = e instanceof Error ? e.message : String(e);
          const errorKey = `${loadKey}:unexpected:${message}`;
          const shouldReport = lastCrossDocErrorKeyRef.current !== errorKey;
          lastCrossDocErrorKeyRef.current = errorKey;
          if (shouldReport) {
            console.warn('[AutomationPanel] Cross-doc row loading error:', e);
          }
          setCrossDocRows(null);
        }
      }
    };

    loadCrossDocRows().catch(console.error);

    return () => {
      cancelled = true;
    };
  }, [
    isCrossDoc,
    config?.sourceDocId,
    config?.sourceTableId,
    config?.sourceTableName,
    config?.filterGroup,
    crossDocAutomationService,
  ]);

  // Same-doc row processing (synchronous)
  const sameDocRows = useMemo(() => {
    if (isCrossDoc || !currentDoc || !config?.sourceTableId) return null;

    const sourceDb = databases.find(
      (db: { blockId: string }) => db.blockId === config.sourceTableId
    );
    if (!sourceDb) return null;

    // Note: currentDoc is a Store but processRowsForExecution works with it
    // The row processor accepts the Store shape used by the active editor doc.
    return processRowsForExecution(
      currentDoc as unknown as Parameters<typeof processRowsForExecution>[0],
      config.sourceTableId,
      sourceDb.databaseTitle,
      config.filterGroup,
      config.selectedColumns,
      {
        // E018-WF1: Pass inputMappings to filter preview to only mapped columns
        inputMappings: config.inputMappings,
        // E018-T006: Pass statusTracking for auto-filter in row count
        statusTracking: config.statusTracking,
      }
    );
  }, [
    isCrossDoc,
    currentDoc,
    config?.sourceTableId,
    config?.filterGroup,
    config?.selectedColumns,
    config?.inputMappings,
    config?.statusTracking,
    databases,
  ]);

  // Combined: use cross-doc rows if cross-doc, otherwise same-doc rows
  const processedRows = isCrossDoc ? crossDocRows : sameDocRows;

  // Get selected workflow info
  const selectedWorkflow = useMemo(() => {
    if (!config?.workflowId) {
      return null;
    }
    const found =
      workflows.find((w: { id: string }) => w.id === config.workflowId) ?? null;
    return found;
  }, [config?.workflowId, workflows]);

  // E018-S007-T001: Single Source of Truth for UI State
  const rowCount = processedRows?.payloads?.length ?? 0;
  const uiState = useAutomationUIState(activeBlockId, rowCount);
  const canPublishAutomationTemplate = canOpenAutomationTemplatePublish({
    publishAllowed,
    canPublish: canPublishMarketplace,
    automationBlockId: activeBlockId,
    executionStatus: uiState.badgeStatus,
  });

  // Action items for execution panel configure hint
  // Must be before ALL early returns to maintain stable hook count
  const hasConfiguredSource =
    config?.triggerConfig?.type === 'webhook'
      ? !!config?.triggerConfig?.webhookCredentialId
      : !!config?.sourceTableId;
  const isConfigured = hasConfiguredSource && !!config?.workflowId;

  // Highlight a section after tab switch — scrolls into view + brief yellow pulse
  const flashHighlight = useCallback((el: HTMLElement) => {
    flashElementOutline(el);
  }, []);

  const highlightSection = useCallback(
    (tab: AutomationTab, sectionSelector: string) => {
      setCurrentTab(tab);
      setTimeout(() => {
        const el = document.querySelector(sectionSelector) as HTMLElement;
        if (el) flashHighlight(el);
      }, 100);
    },
    [setCurrentTab, flashHighlight]
  );

  const executionActionItems = useMemo(() => {
    if (!config) return [];
    const items: Array<{ label: string; onClick: () => void }> = [];
    if (!hasConfiguredSource) {
      items.push({
        label:
          config.triggerConfig?.type === 'webhook'
            ? (t[
                'com.streamlain.streamlain.execution.missing-webhook-source'
              ]?.() ?? 'Select webhook source')
            : (t['com.streamlain.streamlain.execution.missing-source']?.() ??
              'Select a source table'),
        onClick: () =>
          highlightSection(
            AutomationTab.Input,
            '[data-section="input-tables"]'
          ),
      });
    }
    if (!config.workflowId) {
      items.push({
        label:
          t['com.streamlain.streamlain.execution.missing-workflow']?.() ??
          'Select a workflow',
        onClick: () =>
          highlightSection(
            AutomationTab.Process,
            '[data-section="workflow-selector"]'
          ),
      });
    }
    // sl-oe5x: Target-table selection covers both "no table added" and
    // "table added but tableId unset". The generic "Configure output
    // mapping" button that used to fire here was redundant — required
    // output fields are surfaced by the dedicated unmapped-required
    // check below, which points the user at the exact same section.
    if (
      isConfigured &&
      (!config.outputTables?.length ||
        config.outputTables.some((ot: { tableId?: string }) => !ot.tableId))
    ) {
      items.push({
        label:
          t['com.streamlain.streamlain.execution.missing-output-table']?.() ??
          'Select output table',
        onClick: () =>
          highlightSection(
            AutomationTab.Output,
            '[data-section="target-tables"]'
          ),
      });
    }
    if (config.workflowId && selectedWorkflow && !selectedWorkflow.active) {
      items.push({
        label:
          t['com.streamlain.streamlain.execution.unpublished-workflow']?.() ??
          'Publish workflow first',
        onClick: () => {
          const editor = editorService?.editor;
          if (editor && config.workflowId) {
            editor.openWorkflowFullscreen(
              config.workflowId,
              config.workflowName ?? ''
            );
            // Highlight the activation toggle after fullscreen editor renders
            setTimeout(() => {
              const el = document.querySelector(
                '[class*="toggleTrack"]'
              ) as HTMLElement;
              if (el) flashHighlight(el);
            }, 500);
          }
        },
      });
    }
    if (config.statusTracking?.enabled && !config.statusTracking.columnId) {
      items.push({
        label:
          t[
            'com.streamlain.streamlain.validation.status-tracking-incomplete'
          ]?.() ?? 'Select a status column',
        onClick: () =>
          highlightSection(
            AutomationTab.Input,
            '[data-section="status-tracking"]'
          ),
      });
    }
    // Check for unmapped required input fields
    if (workflowSchema?.input?.required?.length) {
      const mappings = config.inputMappings ?? [];
      const mappedFieldIds = new Set(
        mappings
          .filter(m => m.schemaFieldId && m.columnId)
          .map(m => m.schemaFieldId)
      );
      const unmappedRequired = workflowSchema.input.required.filter(
        f => !mappedFieldIds.has(f.id)
      );
      if (unmappedRequired.length > 0) {
        items.push({
          label:
            t[
              'com.streamlain.streamlain.execution.missing-input-mapping'
            ]?.() ??
            `Map required input fields (${unmappedRequired.map(f => f.field).join(', ')})`,
          onClick: () =>
            highlightSection(
              AutomationTab.Input,
              // Highlight the full section wrapper (header + body), not just
              // the grid body — consistent with the other execution-action
              // buttons above, which all target `[data-section="..."]`.
              '[data-section="fields"]'
            ),
        });
      }
    }
    // Check for unmapped required output fields (symmetric to input).
    // fixedValue bypasses the workflow response for that column, so it does
    // NOT satisfy a required output — the workflow-erzeugte Pflichtwert muss
    // via responseField in mindestens eine Spalte geschrieben werden.
    if (isConfigured && workflowSchema?.output) {
      const requiredOutputs = Object.values(workflowSchema.output).filter(
        f => f.required
      );
      if (requiredOutputs.length > 0) {
        const mappings = config.outputMappings ?? [];
        const mappedResponseFields = new Set(
          mappings.map(m => m.responseField).filter(Boolean)
        );
        const unmappedRequired = requiredOutputs.filter(
          f => !mappedResponseFields.has(f.id)
        );
        if (unmappedRequired.length > 0) {
          items.push({
            label:
              t[
                'com.streamlain.streamlain.execution.missing-output-mapping'
              ]?.() ??
              `Map required output fields (${unmappedRequired.map(f => f.name).join(', ')})`,
            onClick: () =>
              highlightSection(
                AutomationTab.Output,
                '[data-section="field-mapping"]'
              ),
          });
        }
      }
    }
    return items;
  }, [
    config,
    hasConfiguredSource,
    isConfigured,
    selectedWorkflow,
    workflowSchema,
    t,
    editorService,
    highlightSection,
    flashHighlight,
  ]);

  // sl-1ahs.6.4: Cached execution-response state removed together with the
  // Test Run infrastructure (sl-1ahs.7.2). Output mappings are now derived
  // from the workflow End node output schema (sl-1ahs.7.3) instead of from
  // a remembered response object, so the panel no longer needs to subscribe
  // to execution logs or persist anything onto blockModel.props.

  // Get ALL input table columns for resolving column IDs to names in response preview
  // sl-i6t9-fix: Include source + linked/secondary table columns so that linked table
  // column IDs (e.g. btki6AZb2U) are resolved to human-readable names in field mapping.
  const sourceColumns = useMemo((): ColumnInfo[] => {
    if (!config?.sourceTableId) return [];
    const allColumns: ColumnInfo[] = [];
    const seenIds = new Set<string>();

    // Source table columns
    const sourceDb = databases.find(
      (db: { blockId: string }) => db.blockId === config.sourceTableId
    );
    for (const col of sourceDb?.columns ?? []) {
      if (!seenIds.has(col.id)) {
        allColumns.push(col);
        seenIds.add(col.id);
      }
    }

    // Linked/secondary input table columns
    for (const inputTable of config.inputTables ?? []) {
      if (!inputTable.tableId || inputTable.tableId === config.sourceTableId)
        continue;
      const db = databases.find(
        (d: { blockId: string }) => d.blockId === inputTable.tableId
      );
      for (const col of db?.columns ?? []) {
        if (!seenIds.has(col.id)) {
          allColumns.push(col);
          seenIds.add(col.id);
        }
      }
    }

    return allColumns;
  }, [config?.sourceTableId, config?.inputTables, databases]);

  // Invalidate cached response structure when the linked workflow is re-published
  // (output schema may have changed — stale mappings would reference non-existent fields)
  useEffect(() => {
    let disposed = false;
    const handleWorkflowPublished = (event: Event) => {
      const { workflowId } = (event as CustomEvent<{ workflowId: string }>)
        .detail;
      if (
        !blockModel ||
        !config?.workflowId ||
        config.workflowId !== workflowId
      )
        return;

      workflowService.invalidateSchemaCache(workflowId);
      workflowService.selectedWorkflowSchema$.next(null);
      // Re-fetch schema immediately so sidebar updates without page refresh
      void workflowService.loadWorkflowSchema(workflowId);

      // Refresh the compact canvas projection only for the active automation.
      // Other canvas blocks remain entirely local-first and make no requests.
      workflowService.invalidateWorkflowStepSummaries(workflowId);
      void workflowService
        .loadWorkflowStepSummaries(workflowId, true)
        .then(workflowSteps => {
          if (disposed || !currentDoc || !blockModel) return;
          const props = blockModel.props as Record<string, unknown>;
          if (props.workflowId !== workflowId) return;
          currentDoc.transact(() => {
            props.workflowSteps = workflowSteps;
            props.workflowStepsSourceId = workflowId;
          });
        })
        .catch(error => {
          console.error(
            '[AutomationPanel] Failed to refresh workflow-step cache:',
            error
          );
        });
    };

    // When workflow is renamed, update block title + workflowName prop
    const handleWorkflowRenamed = (event: Event) => {
      const { workflowId, name: newName } = (
        event as CustomEvent<{ workflowId: string; name: string }>
      ).detail;
      if (
        !blockModel ||
        !config?.workflowId ||
        config.workflowId !== workflowId
      )
        return;
      writeAutomationTitle(
        blockModel as { props: Record<string, unknown> } | null,
        newName,
        'workflow'
      );
      const props = blockModel.props as Record<string, unknown>;
      props.workflowName = newName;
    };

    window.addEventListener('workflow:published', handleWorkflowPublished);
    window.addEventListener('workflow:renamed', handleWorkflowRenamed);
    return () => {
      disposed = true;
      window.removeEventListener('workflow:published', handleWorkflowPublished);
      window.removeEventListener('workflow:renamed', handleWorkflowRenamed);
    };
  }, [blockModel, config?.workflowId, currentDoc, workflowService]);

  // E018-S008-T005: Sync database names when they change (CrossDoc reactive updates)
  // This keeps sourceTableName and targetTableName in sync with actual database titles
  useEffect(() => {
    if (!blockModel || !currentDoc || !config) return;

    const props = blockModel.props as Record<string, unknown>;
    let needsUpdate = false;
    // sl-4n0: Changed from Record<string, string> to support outputTables array
    const updates: Record<string, unknown> = {};

    // sl-1ahs.8.1: Webhook triggers may also have a source table (sl-1ahs.2.1
    // removed the UI guards), so name-sync must apply for all trigger types.
    if (config.sourceTableId) {
      const sourceDb = databases.find(
        (db: { blockId: string }) => db.blockId === config.sourceTableId
      );
      if (sourceDb && sourceDb.databaseTitle !== config.sourceTableName) {
        updates.sourceTableName = sourceDb.databaseTitle;
        needsUpdate = true;
      }
    }

    // sl-4n0: Check output table names
    if (config.outputTables && config.outputTables.length > 0) {
      const updatedOutputTables = config.outputTables.map(outputTable => {
        const targetDb = databases.find(
          (db: { blockId: string }) => db.blockId === outputTable.tableId
        );
        if (targetDb && targetDb.databaseTitle !== outputTable.tableName) {
          needsUpdate = true;
          return { ...outputTable, tableName: targetDb.databaseTitle };
        }
        return outputTable;
      });
      if (needsUpdate) {
        updates.outputTables = updatedOutputTables;
      }
    }

    // Apply updates if needed
    if (needsUpdate) {
      currentDoc.transact(() => {
        for (const [key, value] of Object.entries(updates)) {
          props[key] = value;
        }
      });
    }
  }, [blockModel, currentDoc, config, currentDocId, databases]);

  // Keep stored doc IDs in sync when a referenced database is discovered in a
  // different doc. Missing entries in database discovery are intentionally not
  // treated as deletion: discovery is local/transient during sync, reload, and
  // execution refreshes. Nulling persisted config from that signal can destroy
  // valid source/output references.
  useEffect(() => {
    if (!blockModel || !currentDoc || !config) return;

    const props = blockModel.props as Record<string, unknown>;
    const updates = computeDatabaseReferenceMoveUpdates(
      config,
      databases,
      currentDocId
    );

    // Apply updates if any database was moved
    if (updates) {
      currentDoc.transact(() => {
        for (const [key, value] of Object.entries(updates)) {
          props[key] = value;
        }
      });
    }
  }, [blockModel, currentDoc, config, currentDocId, databases]);

  // Get target columns for Output Tab mapping (single table mode)
  // sl-trm: Read mode from first outputTable (per-table mode, no global legacy)
  const targetColumns = useMemo((): ColumnInfo[] => {
    if (!config) return [];

    const primaryOutputTable = config.outputTables?.[0];

    // For update_source mode, use source table columns
    if (primaryOutputTable?.mode === 'update_source') {
      const sourceDb = databases.find(
        (db: { blockId: string }) => db.blockId === config.sourceTableId
      );
      return sourceDb?.columns ?? [];
    }

    // For append/other modes, use primary output table columns
    if (primaryOutputTable) {
      const targetDb = databases.find(
        (db: { blockId: string }) => db.blockId === primaryOutputTable.tableId
      );
      return targetDb?.columns ?? [];
    }

    return [];
  }, [config, databases]);

  // sl-3nk: Map of tableId -> columns for multi-table field mapping
  const tableColumnsMap = useMemo((): Map<string, ColumnInfo[]> => {
    const map = new Map<string, ColumnInfo[]>();
    if (!config?.outputTables) return map;

    for (const outputTable of config.outputTables) {
      if (!outputTable.tableId) continue;
      const db = databases.find(
        (d: { blockId: string }) => d.blockId === outputTable.tableId
      );
      if (db?.columns) {
        map.set(outputTable.tableId, db.columns);
      }
    }
    return map;
  }, [config?.outputTables, databases]);

  // S020-8.20.6: Get columns used by other automations for conflict detection
  const usedColumns = useMemo(() => {
    if (!activeBlockId || !config?.sourceTableId) {
      return new Map();
    }
    return automationDiscoveryService.getUsedColumns(
      activeBlockId,
      config.sourceTableId
    );
  }, [activeBlockId, config?.sourceTableId, automationDiscoveryService]);

  // S020-8.20.6: Navigate to conflicting automation with cinematic effect
  const handleNavigateToConflict = useCallback(
    async (automationId: string, _docId: string) => {
      // Find the automation to get its position
      const automation =
        automationDiscoveryService.findAutomation(automationId);
      if (!automation || !automation.xywh) {
        console.warn(
          '[AutomationPanel] Cannot navigate to conflict - automation not found or no position'
        );
        return;
      }

      // Parse xywh to get position
      const [x, y] = automation.xywh.split(',').map(Number);
      if (isNaN(x) || isNaN(y)) {
        console.warn('[AutomationPanel] Invalid xywh:', automation.xywh);
        return;
      }

      // Get viewport for cinematic navigation
      const edgelessRoot = document.querySelector('affine-edgeless-root');
      const gfx = (edgelessRoot as any)?.gfx;

      if (gfx?.viewport) {
        // Import dynamically to avoid circular deps
        const { cinematicNavigateTo } =
          await import('../utils/cinematic-viewport');
        await cinematicNavigateTo(gfx.viewport, x, y, {
          duration: 1000,
          minZoom: 0.75,
        });

        // Set the automation as active to open its sidebar
        automationService.setActiveBlock(automationId);
      }
    },
    [automationDiscoveryService, automationService]
  );

  // sl-trigger-removal.5: Trigger sync useEffect removed - scheduling now handled via ConfigService/ScheduleService

  // E018-S007-T001: Update BlockModel props directly
  // NO setConfig needed - useBlockModelConfig hook auto-updates via propsUpdated subscription
  const handleUpdate = useCallback(
    (updates: Partial<AutomationConfig>) => {
      if (!blockModel || !currentDoc) {
        console.warn('[AutomationPanel] No blockModel or currentDoc available');
        return;
      }
      // sl-za0v.10: RBAC — block config edits without canEdit permission
      if (!wfPermissions.canEdit) {
        console.warn(
          '[AutomationPanel] Config update blocked by RBAC (canEdit=false)'
        );
        return;
      }
      console.log('[AutomationPanel] handleUpdate:', JSON.stringify(updates));
      currentDoc.transact(() => {
        const props = blockModel.props as Record<string, unknown>;

        // Decide ownership against the previous workflow identity. Applying
        // workflowName first would misclassify legacy auto titles as custom.
        if (updates.workflowName !== undefined) {
          writeAutomationTitle(
            blockModel as { props: Record<string, unknown> } | null,
            (updates.workflowName as string) || '',
            'workflow'
          );
        }

        // Apply updates
        for (const [key, value] of Object.entries(updates)) {
          props[key] = value;
        }
      });

      // Backend sync on workflowId change is handled at editor level
      // (blocksuite-editor.tsx blockUpdated listener) — fires for ALL assignment
      // paths (sidebar, workflow builder, programmatic).
      // E018-S007-T001: No setConfig needed - hook auto-re-renders via propsUpdated
    },
    [blockModel, currentDoc, wfPermissions.canEdit]
  );

  const handleCreateStatusColumn = useCallback(async () => {
    if (!config?.sourceTableId || !currentDoc) {
      notify.error({
        title:
          t['com.streamlain.streamlain.execution.missing-source']?.() ??
          'Select a source table',
      });
      return;
    }
    if (!wfPermissions.canEdit) {
      notify.error({
        title: t['com.streamlain.no-permission'](),
      });
      return;
    }

    const sourceDocId = config.sourceDocId || currentDocId;
    let releaseSourceDoc: (() => void) | undefined;

    try {
      let sourceDoc = currentDoc;
      if (sourceDocId && sourceDocId !== currentDocId) {
        const opened = docsService.open(sourceDocId);
        releaseSourceDoc = opened.release;
        sourceDoc = opened.doc.blockSuiteDoc;
        sourceDoc.load();
      }

      const sourceBlock = sourceDoc.getBlock(config.sourceTableId);
      const sourceModel = sourceBlock?.model;
      if (!sourceModel) {
        throw new Error('Source database block not found.');
      }

      const columnName =
        config.statusTracking?.createColumn?.name?.trim() ||
        DEFAULT_STATUS_TRACKING_CONFIG.createColumn?.name ||
        'Status';
      const standardOptions = config.statusTracking?.standardOptions?.length
        ? config.statusTracking.standardOptions
        : DEFAULT_STATUS_TRACKING_CONFIG.standardOptions;
      let columnId: string | undefined;

      sourceDoc.transact(() => {
        const dataSource = createDatabaseBlockDataSource(sourceModel as any);
        const activeView = dataSource.viewManager.currentView$.value;
        const propertyOwner = activeView ?? dataSource;
        columnId = propertyOwner.propertyAdd('end', {
          type: 'select',
          name: columnName,
        });
        if (!columnId) {
          throw new Error('Status column could not be created.');
        }
        dataSource.propertyDataSet(columnId, {
          options: createSelectColumnOptions(standardOptions),
        });
      });

      if (!columnId) {
        throw new Error('Status column could not be created.');
      }

      handleUpdate({
        statusTracking: {
          ...DEFAULT_STATUS_TRACKING_CONFIG,
          ...config.statusTracking,
          enabled: true,
          columnId,
          standardOptions,
        },
      });

      notify.success({
        title: columnName,
      });
    } catch (err) {
      notify.error({
        title:
          t[
            'com.streamlain.streamlain.validation.status-tracking-incomplete'
          ]?.() ?? 'Status tracking is incomplete',
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      releaseSourceDoc?.();
    }
  }, [
    config,
    currentDoc,
    currentDocId,
    docsService,
    handleUpdate,
    t,
    wfPermissions.canEdit,
  ]);

  // Stale workflow cleanup handled at block level via workflow:deleted event
  // (automation-canvas-block.ts) — no sidebar dependency needed.

  // sl-trigger-explicit: Handle schedule toggle - saves/disables schedule on backend
  const handleScheduleToggle = useCallback(
    async (enabled: boolean): Promise<boolean> => {
      if (
        !activeBlockId ||
        !automationInfo?.docId ||
        !workspace?.id ||
        !config
      ) {
        console.warn('[AutomationPanel] Missing context for schedule toggle');
        return false;
      }

      setIsScheduleSaving(true);

      try {
        if (enabled) {
          // Step 1: Validate workflow is selected
          const workflowId = config.workflowId;
          if (!workflowId) {
            console.error('[AutomationPanel] No workflow selected');
            return false;
          }

          // Step 2: Flush the same page/root/row-shard document set used by
          // the execution preflight. A schedule can fire immediately after
          // activation, so persisting only the automation config allows its
          // first tick to observe an empty source database.
          await flushPreFlightDocIds(
            workspace.engine.doc,
            collectPreFlightDocIds(
              automationInfo.docId,
              config,
              'execute',
              databases.map(database => ({
                tableId: database.blockId,
                docId: database.docId,
                databaseRootDocId: database.databaseRootDocId,
                databaseRowShardDocIds: database.databaseRowShardDocIds,
              })),
              workspace.id
            )
          );

          // Step 3: Save config first (required before schedule can be created)
          // Backend resolves webhook URL from workflow via Y.js sync
          const savedConfig = await configApiService.saveConfig(
            activeBlockId,
            workspace.id,
            {
              docId: automationInfo.docId,
            }
          );

          if (!savedConfig) {
            console.error('[AutomationPanel] Failed to save config');
            return false;
          }

          console.log('[AutomationPanel] Config saved:', savedConfig.id);

          // Step 4: Persist schedule configuration and activation together.
          // The backend owns the authoritative topology projection and enable
          // decision. Polling the same boundary here first made activation
          // wait through the projection twice.
          const scheduleResult = await configApiService.saveSchedule(
            activeBlockId,
            workspace.id,
            automationInfo.docId,
            {
              triggerType: config.triggerConfig?.type ?? 'scheduled',
              cronPreset: config.triggerConfig?.cronPreset,
              timezone: config.triggerConfig?.timezone,
              enabled: true,
            }
          );

          if (!scheduleResult) {
            console.error('[AutomationPanel] Failed to save schedule');
            return false;
          }

          console.log('[AutomationPanel] Schedule saved:', scheduleResult.id);
          window.__automationRuntimeBridge?.setSchedule(
            activeBlockId,
            scheduleResult
          );

          console.log('[AutomationPanel] Schedule enabled and persisted');
          return true;
        } else {
          const scheduleResult = await configApiService.saveSchedule(
            activeBlockId,
            workspace.id,
            automationInfo.docId,
            {
              triggerType: config.triggerConfig?.type ?? 'scheduled',
              cronPreset: config.triggerConfig?.cronPreset,
              timezone: config.triggerConfig?.timezone,
              enabled: false,
            }
          );
          if (!scheduleResult) return false;
          window.__automationRuntimeBridge?.setSchedule(
            activeBlockId,
            scheduleResult
          );
          console.log('[AutomationPanel] Schedule disabled and persisted');
          return true;
        }
      } catch (error) {
        console.error('[AutomationPanel] Schedule toggle error:', error);
        notify.error({
          title: t['com.streamlain.automation-safety.enable-denied'](),
          message: t[automationSafetyEnableFailureKey(error)](),
        });
        return false;
      } finally {
        setIsScheduleSaving(false);
      }
    },
    [
      activeBlockId,
      automationInfo?.docId,
      workspace?.id,
      config,
      configApiService,
      databases,
      t,
      workspace?.engine.doc,
    ]
  );

  const handleHeaderScheduleToggle = useCallback(async () => {
    if (!config) return;
    if (!wfPermissions.canEdit) {
      notify.warning({
        title:
          t['com.streamlain.streamlain.execution.notify.edit-denied-title'](),
        message:
          t['com.streamlain.streamlain.execution.notify.edit-denied-message'](),
      });
      return;
    }

    const newEnabled = !config.triggerConfig?.enabled;
    if (newEnabled) {
      const statusTracking = config.statusTracking;
      if (!statusTracking?.enabled) {
        notify.error({
          title:
            t['com.streamlain.streamlain.validation.cannot-activate-trigger'](),
          message:
            t[
              'com.streamlain.streamlain.validation.status-tracking-required'
            ](),
        });
        highlightSection(
          AutomationTab.Input,
          '[data-section="status-tracking"]'
        );
        return;
      }
      if (!statusTracking.columnId) {
        notify.error({
          title:
            t['com.streamlain.streamlain.validation.cannot-activate-trigger'](),
          message:
            t[
              'com.streamlain.streamlain.validation.status-tracking-incomplete'
            ](),
        });
        highlightSection(
          AutomationTab.Input,
          '[data-section="status-tracking"]'
        );
        return;
      }
    }

    const success = await handleScheduleToggle(newEnabled);
    if (!success) return;

    handleUpdate({
      triggerConfig: {
        type: config.triggerConfig?.type || 'scheduled',
        cronPreset: config.triggerConfig?.cronPreset || 'hourly',
        ...config.triggerConfig,
        enabled: newEnabled,
      },
    });
    const notification = {
      title: newEnabled
        ? t['com.streamlain.streamlain.process.schedule-active-info']()
        : t['com.streamlain.streamlain.process.schedule-inactive-info'](),
    };
    if (newEnabled) {
      notify.success(notification);
    } else {
      notify.warning(notification);
    }
  }, [
    config,
    handleScheduleToggle,
    handleUpdate,
    highlightSection,
    t,
    wfPermissions.canEdit,
  ]);

  const handleTabChange = useCallback((value: string) => {
    setCurrentTab(value);
  }, []);

  const executionRows = useMemo(
    () => processedRows?.payloads ?? [],
    [processedRows?.payloads]
  );

  // E024-S001-T001: Local Storage Restriction - Show Cloud-Only Message
  // This blocks ALL automation configuration in Local Storage workspaces
  if (isLocalStorage) {
    const buttonLabel = isLoggedIn
      ? t['Enable AFFiNE Cloud']()
      : t['Sign in and Enable']();

    return (
      <div className={styles.panel}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            padding: '48px 24px',
            textAlign: 'center',
            gap: '16px',
          }}
        >
          <svg
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ opacity: 0.5 }}
          >
            <path
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
              fill="currentColor"
            />
          </svg>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
            Automations require {BRAND_DISPLAY_NAME} Cloud
          </h3>
          <p
            style={{
              margin: 0,
              fontSize: '14px',
              color: 'var(--streamlain-text-secondary-color)',
              maxWidth: '400px',
            }}
          >
            Your local data is stored in your browser and cannot run automated
            workflows. Enable {BRAND_DISPLAY_NAME} Cloud to use Automations.
          </p>
          <Button
            onClick={handleEnableCloud}
            style={{
              background: 'var(--streamlain-background-secondary-color)',
              border: '1px solid var(--streamlain-border-color)',
              color: 'var(--streamlain-text-primary-color)',
            }}
          >
            {buttonLabel}
          </Button>
        </div>
      </div>
    );
  }

  // Empty state - no automation block selected, show workspace overview
  if (!activeBlockId || !config) {
    return (
      <div className={styles.panel}>
        <WorkspaceAutomationsPanel />
      </div>
    );
  }

  // E018-S005-T001: Onboarding Mode - show guided workflow selection for new automations
  const isOnboarding = !config.workflowId;

  const handleOnboardingConfirm = (workflow: WorkflowInfo) => {
    // Keep an untouched automation auto-named from its selected workflow.
    // webhookUrl is stored to avoid race condition with WorkflowService.workflows$ loading
    handleUpdate({
      workflowId: workflow.id,
      workflowName: workflow.name,
      workflowSteps: [],
      workflowStepsSourceId: null,
      webhookUrlOverride: workflow.webhookUrl || null, // Store for execution
    });
    void workflowService
      .loadWorkflowStepSummaries(workflow.id)
      .then(workflowSteps => {
        const props = blockModel?.props as Record<string, unknown> | undefined;
        if (props?.workflowId !== workflow.id) return;
        handleUpdate({
          workflowSteps,
          workflowStepsSourceId: workflow.id,
        });
      })
      .catch(error => {
        console.error(
          '[AutomationPanel] Failed to cache selected workflow steps:',
          error
        );
      });
  };

  if (isOnboarding) {
    return (
      <OnboardingView
        onBack={() => automationService.setActiveBlock(null)}
        titleEditor={detailTitleEditor}
        onConfirm={handleOnboardingConfirm}
        onChange={handleOnboardingConfirm} // DRY: Same handler for immediate updates
      />
    );
  }

  return (
    <div ref={panelRef} className={styles.panel}>
      {isScheduleSaving && (
        <div
          className={styles.scheduleSavingOverlay}
          role="status"
          aria-live="polite"
          aria-label={t['com.streamlain.streamlain.common.saving']()}
        >
          <span className={styles.scheduleSavingSpinner} aria-hidden="true" />
          <strong>{t['com.streamlain.streamlain.common.saving']()}</strong>
          <span>
            {t['com.streamlain.streamlain.process.schedule-active-info']()}
          </span>
        </div>
      )}
      {/* Header with Back Button and Enable Toggle */}
      <div className={styles.header}>
        <button
          className={styles.backButton}
          onClick={() => automationService.setActiveBlock(null)}
          title={t['com.streamlain.streamlain.common.back-to-overview']()}
        >
          <ArrowLeftSmallIcon width={20} height={20} />
        </button>
        {detailTitleEditor}
        {/* Enable/Disable Toggle — sl-1ahs.4.1: only active for scheduled triggers */}
        {(() => {
          const isScheduledTrigger =
            config?.triggerConfig?.type === 'scheduled';
          const toggleDisabled = !isScheduledTrigger;
          return (
            <div
              className={styles.headerScheduleControl}
              title={
                toggleDisabled
                  ? (t[
                      'com.streamlain.streamlain.process.toggle-only-scheduled'
                    ]?.() ?? 'Only available for scheduled triggers')
                  : undefined
              }
            >
              <span
                style={{
                  fontSize: '12px',
                  color: config?.triggerConfig?.enabled
                    ? 'var(--streamlain-success-color)'
                    : 'var(--streamlain-text-secondary-color)',
                  opacity: toggleDisabled ? 0.5 : 1,
                }}
              >
                {config?.triggerConfig?.enabled
                  ? t['com.streamlain.streamlain.common.active']()
                  : t['com.streamlain.streamlain.common.inactive']()}
              </span>
              <button
                disabled={toggleDisabled}
                onClick={() => void handleHeaderScheduleToggle()}
                title={
                  toggleDisabled
                    ? (t[
                        'com.streamlain.streamlain.process.toggle-only-scheduled'
                      ]?.() ?? 'Only available for scheduled triggers')
                    : config?.triggerConfig?.enabled
                      ? t[
                          'com.streamlain.streamlain.common.disable-automation'
                        ]()
                      : t[
                          'com.streamlain.streamlain.common.enable-automation'
                        ]()
                }
                style={{
                  width: '44px',
                  height: '24px',
                  borderRadius: '12px',
                  border: 'none',
                  backgroundColor: config?.triggerConfig?.enabled
                    ? 'var(--streamlain-success-color)'
                    : 'var(--streamlain-border-color)',
                  cursor: toggleDisabled ? 'not-allowed' : 'pointer',
                  position: 'relative',
                  transition: 'background-color 0.2s',
                  opacity: toggleDisabled ? 0.5 : 1,
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: '2px',
                    left: config?.triggerConfig?.enabled ? '22px' : '2px',
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: 'white',
                    transition: 'left 0.2s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  }}
                />
              </button>
            </div>
          );
        })()}
      </div>

      {/* Scrollable Content Area */}
      <div className={styles.scrollableContent}>
        {safetyDiagnostic && safetyDiagnostic.topology.state !== 'SAFE' ? (
          <div
            role={
              safetyDiagnostic.topology.state === 'EXTERNAL_UNVERIFIABLE'
                ? 'status'
                : 'alert'
            }
            aria-live="polite"
            style={{
              margin: '12px 16px 0',
              padding: '12px',
              border: '1px solid var(--streamlain-border-color)',
              borderRadius: '8px',
            }}
          >
            <strong>
              {t[
                presentAutomationSafety(
                  safetyDiagnostic.topology.state,
                  safetyDiagnostic.actions
                ).titleKey
              ]()}
            </strong>
            {safetyDiagnostic.incidents[0]?.path.length ? (
              <ol aria-label="Automation cycle path">
                {safetyDiagnostic.incidents[0].path.map((segment, index) => (
                  <li key={`${segment.configId ?? 'opaque'}:${index}`}>
                    {segment.opaque
                      ? t['com.streamlain.automation-safety.restricted-step']()
                      : (segment.displayName ??
                        segment.stepId ??
                        segment.configId ??
                        segment.resourceType)}
                  </li>
                ))}
              </ol>
            ) : null}
          </div>
        ) : null}
        {/* sl-trigger-removal.5: Owner & Notification sections removed - will be restored via ConfigService */}

        {/* sl-ufq6.3.2: Budget Group Assignment */}
        <AutomationGroupSelector
          workspaceId={workspace.id}
          automationBlockId={activeBlockId ?? undefined}
        />

        {/* Tabs - sl-2w9: Using RadioGroup for animated tab indicator */}
        <div className={styles.tabsRoot}>
          <RadioGroup
            value={currentTab}
            onChange={handleTabChange}
            items={tabItems}
            width="100%"
            padding={4}
            borderRadius={8}
            gap={4}
            itemHeight={36}
          />

          {/* Tab Content */}
          <div className={styles.tabsContent}>
            {currentTab === AutomationTab.Input && (
              <InputTab
                config={config}
                onUpdate={handleUpdate}
                sourceColumns={sourceColumns}
                accessibleDatabases={databases}
                usedColumns={usedColumns}
                onNavigateToConflict={(automationId, docId) => {
                  handleNavigateToConflict(automationId, docId).catch(
                    console.error
                  );
                }}
                onScheduleToggle={handleScheduleToggle}
                isScheduleSaving={isScheduleSaving}
                blockId={activeBlockId ?? undefined}
                onHighlightStatusTracking={() =>
                  highlightSection(
                    AutomationTab.Input,
                    '[data-section="status-tracking"]'
                  )
                }
                onCreateStatusColumn={handleCreateStatusColumn}
              />
            )}

            {currentTab === AutomationTab.Process && (
              <ProcessTab
                config={config}
                onUpdate={handleUpdate}
                blockId={activeBlockId}
              />
            )}

            {currentTab === AutomationTab.Output && (
              <OutputTab
                config={config}
                onUpdate={handleUpdate}
                targetColumns={targetColumns}
                tableColumnsMap={tableColumnsMap}
                sourceColumns={sourceColumns}
                // sl-3nk: Only owner can configure AI relation matching
                isOwner={config.createdBy === currentUserId}
              />
            )}
          </div>
        </div>
      </div>

      {/* Sticky Footer with Execution, History and Publish sections */}
      <SidebarExpandableGroup ref={footerRef} placement="bottom">
        {/* Execution Section */}
        <SidebarExpandableSection
          expanded={executionExpanded}
          onExpandedChange={setExecutionExpanded}
          placement="bottom"
          title={t['com.streamlain.streamlain.execution.title']()}
          action={
            uiState.badgeStatus === 'ready' &&
            isConfigured &&
            executionActionItems.length === 0 ? (
              <StatusBadge status={uiState.badgeStatus} />
            ) : null
          }
        >
          <ExecutionPanel
            blockId={activeBlockId}
            isConfigured={isConfigured}
            rows={executionRows}
            processedRows={processedRows}
            workflow={selectedWorkflow}
            config={config}
            accessibleDatabases={databases}
            canExecute={wfPermissions.canRun}
            actionItems={executionActionItems}
          />
        </SidebarExpandableSection>

        {/* History Section */}
        <SidebarExpandableSection
          expanded={historyExpanded}
          onExpandedChange={setHistoryExpanded}
          placement="bottom"
          title={t['com.streamlain.streamlain.execution.history']()}
          contentStyle={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minHeight: 0,
          }}
        >
          <HistoryPanel blockId={activeBlockId} />
        </SidebarExpandableSection>

        {/* sl-u319.1.3: Publish Automation Section */}
        {publishAllowed && canPublishMarketplace && (
          <SidebarExpandableSection
            expanded={publishExpanded}
            onExpandedChange={setPublishExpanded}
            placement="bottom"
            title={t['com.streamlain.marketplace.publish.title.new']()}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                padding: '8px 16px',
              }}
            >
              <div
                style={{
                  fontSize: '12px',
                  color: cssVarV2('text/secondary'),
                }}
              >
                {t['com.streamlain.streamlain.publish.description']({
                  title: config?.title ?? '',
                })}
              </div>
              {!canPublishAutomationTemplate && (
                <div
                  style={{
                    fontSize: '12px',
                    color: cssVarV2('text/secondary'),
                  }}
                >
                  {t['com.streamlain.streamlain.publish.needs-run']()}
                </div>
              )}
              <button
                type="button"
                onClick={() => setPublishWizardOpen(true)}
                disabled={!canPublishAutomationTemplate}
                title={
                  canPublishAutomationTemplate
                    ? t['com.streamlain.marketplace.publish.title.new']()
                    : t['com.streamlain.streamlain.publish.needs-run']()
                }
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#fff',
                  backgroundColor: cssVarV2('button/primary'),
                  border: 'none',
                  borderRadius: '6px',
                  cursor: canPublishAutomationTemplate
                    ? 'pointer'
                    : 'not-allowed',
                  opacity: canPublishAutomationTemplate ? 1 : 0.5,
                  alignSelf: 'flex-start',
                }}
              >
                {t['com.streamlain.marketplace.publish.title.new']()}
              </button>
              {publishWizardOpen && workspace.id && activeBlockId && (
                <PublishWizard
                  open={publishWizardOpen}
                  onClose={() => setPublishWizardOpen(false)}
                  input={buildAutomationTemplatePublishSource({
                    workspaceId: workspace.id,
                    automationBlockId: activeBlockId,
                    workflowId: config?.workflowId ?? undefined,
                    title: config?.title ?? '',
                  })}
                />
              )}
            </div>
          </SidebarExpandableSection>
        )}
      </SidebarExpandableGroup>
    </div>
  );
};
